<template>
  <div>
    <div class="row">
      <div class="col-sm-6">
        <label>Zeitraum</label>
        <div class="form-group has-label">
          <el-date-picker v-model="dateRange" type="daterange" range-separator="Bis" start-placeholder="Anfangsdatum"
            end-placeholder="Enddatum" format="dd.MM.yyyy" value-format="yyyy-MM-dd" @change="fetchTableData"
            :picker-options="{ firstDayOfWeek: 1 }" />
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-sm-6" v-if="productCategories.length > 0">
        <label>Product Kategorie</label>
        <div class="form-group has-label">
          <multiselect v-model="selectedProductCategories" :multiple="true" :options="productCategories" track-by="id"
            label="name" @input="fetchTableData" />
        </div>
      </div>
    </div>
    <div class="row">
      <div class="col-sm-6">
        <product-select v-model="product" @input="fetchTableData" />
      </div>
    </div>
    <div style="overflow-x: auto; margin-top: 0.75em;">
      <table class="table table-striped table-bordered">
        <thead>
          <tr>
            <th class="sortable" @click="sortBy('name')">
              Produkt
              <i class="fas" :class="getSortIcon('name')"></i>
            </th>
            <th class="sortable" @click="sortBy('product_category.name')">
              Kategorie
              <i class="fas" :class="getSortIcon('product_category.name')"></i>
            </th>
            <th class="sortable text-right" @click="sortBy('quantity')">
              Lagerstand
              <i class="fas" :class="getSortIcon('quantity')"></i>
            </th>
            <th class="sortable text-right" @click="sortBy('last_rotation')">
              Letzte Bew.
              <i class="fas" :class="getSortIcon('last_rotation')"></i>
            </th>
            <th class="sortable text-right" @click="sortBy('standard_price')">
              Wert (EK)
              <i class="fas" :class="getSortIcon('standard_price')"></i>
            </th>
          </tr>
        </thead>
        <transition name="el-fade-in-linear">
          <tbody v-if="!loading">
            <tr v-for="product in products" :key="product.id">
              <td>
                <router-link :to="`/products/${product.id}`">
                  {{ product.name }}
                </router-link>
              </td>
              <td>{{ product.product_category ? product.product_category.name : '' }}</td>
              <td class="text-right">{{ product.quantity }}</td>
              <td class="text-right">{{ product.last_rotation | date }}</td>
              <td class="text-right">{{ (product.standard_price*product.quantity/100).toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2}) }} €</td>
            </tr>
          </tbody>
        </transition>
      </table>
    </div>
  </div>
</template>

<script>
import { DatePicker } from 'element-ui'
import moment from 'moment'
import ProductSelect from 'src/components/product-select'

export default {
  components: {
    ProductSelect,
    [DatePicker.name]: DatePicker
  },
  data () {
    return {
      dateRange: [
        moment().subtract(6, 'months').startOf('month').format('YYYY-MM-DD'),
        moment().endOf('month').format('YYYY-MM-DD')
      ],
      products: [],
      productCategories: [],
      selectedProductCategories: [],
      product: null,
      loading: true,
      sortKey: 'name',
      sortOrder: 'desc'
    }
  },
  methods: {
    fetchTableData () {
      this.loading = true

      const dateRangeFilter = this.dateRange ? encodeURIComponent(this.dateRange) : ''
      const productCategories = this.selectedProductCategories.length ? encodeURIComponent(this.selectedProductCategories.map(pc => pc.id)) : ''
      const product = this.product ? this.product.id : ''

      this.$http.get(`/products/unusued?date_range=${dateRangeFilter}&product_categories=${productCategories}&product=${product}`).then(({ data }) => {
        this.products = data
        this.loading = false
        this.sortBy('name')
      })
    },
    sortBy (key) {
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortKey = key
        this.sortOrder = 'asc'
      }

      const getValue = (obj, path) => {
        return path.split('.').reduce((acc, part) => (acc && acc[part] ? acc[part] : ''), obj)
      }

      this.products.sort((a, b) => {
        let aVal = getValue(a, key)
        let bVal = getValue(b, key)

        let comparison = 0

        if (key === 'quantity') {
          aVal = parseFloat(aVal) || 0
          bVal = parseFloat(bVal) || 0
          comparison = aVal - bVal
        }
        else if (key === 'standard_price') {
          // Calculate total value (price * quantity / 100) for sorting
          const aTotal = ((parseFloat(a.standard_price) || 0) * (parseFloat(a.quantity) || 0)) / 100
          const bTotal = ((parseFloat(b.standard_price) || 0) * (parseFloat(b.quantity) || 0)) / 100
          comparison = aTotal - bTotal
        }
        else if (key === 'last_rotation') {
          aVal = aVal ? new Date(aVal) : new Date(0)
          bVal = bVal ? new Date(bVal) : new Date(0)
          comparison = aVal - bVal
        }
        else {
          aVal = aVal ? aVal.toString() : ''
          bVal = bVal ? bVal.toString() : ''
          comparison = aVal.localeCompare(bVal, 'de-DE')
        }

        comparison = this.sortOrder === 'desc' ? -comparison : comparison

        if (comparison === 0 && key !== 'name') {
          const aName = a.name || ''
          const bName = b.name || ''
          return aName.localeCompare(bName, 'de-DE')
        }

        return comparison
      })
    },
    getSortIcon (key) {
      if (this.sortKey !== key) return 'fa-sort'
      return this.sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down'
    }
  },
  created () {
    this.fetchTableData()
    this.$http.get('/product_categories').then(({ data }) => { this.productCategories = data })
  }
}
</script>

<style lang="scss" scoped>
.sortable {
  cursor: pointer;
  position: relative;
  padding-right: 25px !important;
  user-select: none;

  &:hover {
    background-color: var(--app-brand-color) !important;
    opacity: 0.9;
  }

  .fas {
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    opacity: 0.5;
  }

  &:hover .fas {
    opacity: 1;
  }
}
</style>
